import { Link } from 'react-router-dom';
import { Button, Chip, Spinner } from '@heroui/react';
import { Archive, ArrowLeft, Pause, Play, Save, SquarePen, Undo2 } from 'lucide-react';
import { TEMPLATE_STATUS_COLORS, TEMPLATE_STATUS_LABELS } from '@/lib/templateStatus';
import { useTemplateEditPage } from './useTemplateEditPage';
import { TemplateEdit } from './components/TemplateEdit';
import { TemplateTasks } from './components/TemplateTasks';
import { MobilePreview } from './components/MobilePreview';
import { PageContainer } from '@/components/templates/PageContainer';

export function TemplateEditPage() {
  const {
    template,
    clients,
    chainTemplates,
    form,
    mode,
    titleError,
    photos,
    isUploadingPhoto,
    isLoading,
    notFound,
    isSaving,
    isStatusUpdating,
    isStaff,
    isNew,
    isDraft,
    clientTitle,
    nextTemplateTitle,
    setField,
    startEdit,
    cancelEdit,
    handleSave,
    handleStatusChange,
    handleUploadPhoto,
    handleDeletePhoto,
    handleAddTask,
    handleUpdateTask,
    handleDeleteTask,
    handleMoveTask,
    handleArchive,
    isTaskMutating,
  } = useTemplateEditPage();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted">
        <Spinner size="sm" />
        Загрузка…
      </div>
    );
  }

  if (notFound) {
    return <p className="py-20 text-center text-sm text-muted">Шаблон не найден</p>;
  }

  const isEdit = mode === 'edit';
  const status = template?.status;
  const tasks = template?.task_templates ?? [];
  // «Вернуть в черновик» — только если по шаблону 0 визитов за всю историю
  const canReturnToDraft = template != null && template.visits_count === 0;

  return (
    <PageContainer className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/templates"
            className="flex shrink-0 items-center gap-1 text-sm text-muted hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            Шаблоны
          </Link>
          <h2 className="truncate text-2xl font-bold text-foreground">
            {template?.title ?? 'Шаблон'}
          </h2>
          {status && (
            <Chip size="sm" variant="soft" color={TEMPLATE_STATUS_COLORS[status]}>
              {TEMPLATE_STATUS_LABELS[status]}
            </Chip>
          )}
          {template?.isArchived && (
            <Chip color="danger" size="sm" variant="soft">
              В архиве
            </Chip>
          )}
        </div>

        <div className="flex items-center gap-2">
          {isEdit ? (
            <>
              {/* У new «Отмена» сбрасывает несохранённый шаблон целиком */}
              <Button variant="outline" onPress={() => void cancelEdit()}>
                Отмена
              </Button>
              <Button onPress={() => void handleSave()} isDisabled={isSaving}>
                {isSaving ? <Spinner size="sm" /> : <Save className="size-4" />}
                Сохранить
              </Button>
            </>
          ) : (
            <>
              {isDraft && (
                // подсказка на обёртке: у disabled-кнопки hover-события не приходят
                <span
                  title={tasks.length === 0 ? 'Сначала добавьте задачи' : undefined}
                  className="inline-flex"
                >
                  <Button
                    onPress={() => void handleStatusChange('active')}
                    isDisabled={isStatusUpdating || tasks.length === 0}
                  >
                    <Play className="size-4" />
                    Запустить
                  </Button>
                </span>
              )}
              {status === 'active' && (
                <Button
                  variant="outline"
                  onPress={() => void handleStatusChange('paused')}
                  isDisabled={isStatusUpdating}
                >
                  <Pause className="size-4" />
                  Приостановить
                </Button>
              )}
              {status === 'paused' && (
                <Button
                  onPress={() => void handleStatusChange('active')}
                  isDisabled={isStatusUpdating}
                >
                  <Play className="size-4" />
                  Возобновить
                </Button>
              )}
              {(status === 'active' || status === 'paused') && canReturnToDraft && (
                <Button
                  variant="outline"
                  onPress={() => void handleStatusChange('draft')}
                  isDisabled={isStatusUpdating}
                >
                  <Undo2 className="size-4" />
                  Вернуть в черновик
                </Button>
              )}
              {isDraft && (
                <Button variant="outline" onPress={startEdit}>
                  <SquarePen className="size-4" />
                  Редактировать
                </Button>
              )}
              <Button variant="outline" onPress={() => void handleArchive()}>
                <Archive className="size-4" />В архив
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Слева основной контент, справа — живое мобильное превью
          (корпус телефона, без карточки) */}
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-4">
          <TemplateEdit
            form={form}
            setField={setField}
            titleError={titleError}
            readOnly={!isEdit}
            isStaff={isStaff}
            clients={clients}
            chainTemplates={chainTemplates}
            clientTitle={clientTitle}
            nextTemplateTitle={nextTemplateTitle}
            slug={template?.slug}
            photos={photos ?? []}
            isDraft={isDraft}
            isNew={isNew}
            isUploadingPhoto={isUploadingPhoto}
            onUploadPhoto={(file) => void handleUploadPhoto(file)}
            onDeletePhoto={(photoId) => void handleDeletePhoto(photoId)}
          />

          {/* Задачи: добавление с тулбара и правка карточек — мгновенно
              в БД (как фото), без общей кнопки «Сохранить» */}
          <TemplateTasks
            tasks={tasks}
            readOnly={!isEdit}
            isMutating={isTaskMutating}
            onAddTask={(type) => void handleAddTask(type)}
            onUpdateTask={(taskId, patch) => void handleUpdateTask(taskId, patch)}
            onDeleteTask={(taskId) => void handleDeleteTask(taskId)}
            onMoveTask={(taskId, dir) => void handleMoveTask(taskId, dir)}
          />
        </div>

        <MobilePreview
          title={form.title}
          instruction={form.instruction}
          photoUrls={(photos ?? []).map((p) => p.url)}
        />
      </div>
    </PageContainer>
  );
}
